import { ChangeDetectionStrategy, Component, DestroyRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { AssignmentSectionQuestionQuery } from '@examdojo/assignment';
import { ChallengeActivityQuery } from '@examdojo/challenge';
import { debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionSelectorItem, QuestionSelectorUiComponent } from '@examdojo/question';
import { ChallengeActivityQuestionSelectorService } from './challenge-activity-question-selector.service';
import { ChallengeActivityQuestionStatusComponent } from './challenge-activity-question-status.component';

@Component({
  selector: 'dojo-challenge-activity-question-selector',
  standalone: true,
  imports: [QuestionSelectorUiComponent, ChallengeActivityQuestionStatusComponent],
  template: `
    <ng-template #statusIndicator let-status>
      <dojo-challenge-activity-question-status [status]="status" class="mr-3" />
    </ng-template>

    <dojo-question-selector-ui
      [questions]="state.questions"
      [activeQuestionId]="state.activeQuestionId ?? null"
      [statusIndicatorTpl]="statusIndicator"
      (questionClick)="setChallengeQuestion($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeActivityQuestionSelectorComponent {
  constructor(
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityQuestionSelectorService: ChallengeActivityQuestionSelectorService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.scrollToQuestionOnCurrentQuestionChange().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly selector = viewChild(QuestionSelectorUiComponent);

  readonly state = connectState({
    questions: this.challengeActivityQuestionSelectorService.questionsWithAttemptStatuses$,
    activeQuestionId: this.assignmentSectionQuestionQuery.activeQuestionId$,
  });

  setChallengeQuestion(question: QuestionSelectorItem) {
    this.challengeActivityQuestionSelectorService.selectQuestion(question);
  }

  private scrollToQuestionOnCurrentQuestionChange() {
    return this.challengeActivityQuery.activeEntity$.pipe(
      map((activity) => activity?.current_question_id),
      filter(isNotNullish),
      distinctUntilChanged(),
      debounceTime(0),
      tap((currentQuestionId) => {
        const questions = this.state.questions;
        if (questions && Array.isArray(questions)) {
          const activeIndex = questions.findIndex((q) => q.assignmentSectionQuestion.question_id === currentQuestionId);
          if (activeIndex !== -1) {
            this.selector()?.scrollToQuestion(activeIndex);
          }
        }
      }),
    );
  }
}
